import React, { useState, useEffect, useRef } from 'react'; // Import useRef
import * as Cesium from "cesium"; // Import all of Cesium as a namespace
import {
  CzmlDataSource,
  SampledPositionProperty,
  JulianDate,
  ClockRange,
  VelocityOrientationProperty,
  ShadowMode,
  TimeIntervalCollection,
  TimeInterval,
  Cartesian3,
  Cartesian2, // For label offset
  LabelStyle, // For label style
  VerticalOrigin, // For label origin
  NearFarScalar, // For label translucency
  HeightReference, // For model/label height reference
  defined, // For checks
  PinBuilder, // Import PinBuilder
  // ScreenSpaceEventHandler, // No longer needed here
  // ScreenSpaceEventType, // No longer needed here
} from "cesium";
import { useCesiumViewer } from '../../../context/CesiumViewerContext'; // Import context hook
import { mapConfig } from '../../../config/mapConfig'; // Import config

const AnimationManager = ({
  // Callback to pass the specific truck entity ref to track up
  onTruckEntityCreated,
  // Prop to trigger animation loading/unloading
  shouldLoadAnimations,
  onWaypointNotify, // Add new prop for notification callback
}) => {
  // Get viewerRef from context
  const viewerRef = useCesiumViewer();

  // Ref to store references for all active animations { animId: { dataSource, truckEntity, waypoints, tickListener } }
  const animationRefs = useRef({});

  // useEffect for setting up the animation
  useEffect(() => {
    // Use viewerRef from context
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer) {
        return;
    }

    // Flag to prevent setting clock multiple times if multiple animations run
    let clockConfigured = false;

    // --- Cleanup Function Definition ---
    // Define cleanup function first so it's accessible within the effect logic
    const cleanupAnimations = () => {
        if (viewer && !viewer.isDestroyed()) {
            // Remove global tick listener if it exists
            if (animationRefs.current.globalTickListener) {
                viewer.clock.onTick.removeEventListener(animationRefs.current.globalTickListener);
                delete animationRefs.current.globalTickListener; // Remove from refs
            }

            // Iterate through stored animation data and remove entities/sources
            Object.entries(animationRefs.current).forEach(([animId, animData]) => {
                if (animId === 'globalTickListener') return; // Skip the listener entry

                // Clear scheduled timeouts for this animation
                if (animData.timeoutIds && animData.timeoutIds.length > 0) {
                    animData.timeoutIds.forEach(clearTimeout);
                }
                // Remove waypoints
                if (animData.waypoints && animData.waypoints.length > 0) {
                    animData.waypoints.forEach(wp => { if (viewer.entities.contains(wp)) viewer.entities.remove(wp); });
                }
                // Remove truck entity
                if (animData.truckEntity && viewer.entities.contains(animData.truckEntity)) {
                    viewer.entities.remove(animData.truckEntity);
                }
                // Remove data source
                if (animData.dataSource && viewer.dataSources.contains(animData.dataSource)) {
                    viewer.dataSources.remove(animData.dataSource, true); // true to destroy
                }
            });

             // Stop clock animation if we are cleaning up
             if (viewer.clock.shouldAnimate) {
                 viewer.clock.shouldAnimate = false;
             }
             // Optionally reset clock times here if desired

        }
        // Clear the refs object
        animationRefs.current = {};
    };

    // --- Setup Function Definition ---
    // Function to set up a single animation based on its config
    const setupSingleAnimation = async (animConfig) => {
      let localDataSource = null;
      let localTruckEntity = null;
      let localWaypoints = [];
      let localTimeoutIds = []; // Array to store timeout IDs for this animation instance
      // let localTickListener = null; // Remove - Tick listener is now global
      // localTickListener is handled globally now

      try {
        localDataSource = await CzmlDataSource.load(animConfig.czmlPath);
        await viewer.dataSources.add(localDataSource);

        const roadEntity = localDataSource.entities.getById(animConfig.roadEntityId);
        if (!roadEntity || !roadEntity.polyline || !roadEntity.polyline.positions) {
          console.error(`AnimationManager [${animConfig.id}]: Entity with ID ${animConfig.roadEntityId} not found in CZML or has no polyline positions.`);
          if (localDataSource) viewer.dataSources.remove(localDataSource, true); // Clean up loaded source
          return null; // Indicate failure for this animation
        }

        if (roadEntity.polyline) {
          roadEntity.polyline.show = false;
        }

        // --- Extract positions ---
        let cartesianPositions = null;
        const positionsProperty = roadEntity.polyline.positions;
        try {
          const positionsValue = positionsProperty?.getValue(viewer.clock.currentTime);
          if (Array.isArray(positionsValue) && positionsValue.length >= 2 && positionsValue[0] instanceof Cartesian3) {
            cartesianPositions = positionsValue;
          }
        } catch (e) { /* ignore */ }
        if (!cartesianPositions && positionsProperty instanceof Cesium.ConstantProperty) {
          const constantValue = positionsProperty.valueOf();
          if (constantValue && Array.isArray(constantValue.cartographicDegrees)) {
            const degrees = constantValue.cartographicDegrees;
            if (degrees.length >= 6 && degrees.length % 3 === 0) {
              cartesianPositions = Cartesian3.fromDegreesArrayHeights(degrees);
            }
          }
        }
        if (!Array.isArray(cartesianPositions) || cartesianPositions.length < 2) {
          console.error(`AnimationManager [${animConfig.id}]: Could not extract valid Cartesian3 positions for entity ${animConfig.roadEntityId}.`);
          if (localDataSource) viewer.dataSources.remove(localDataSource, true); // Clean up loaded source
          return null; // Indicate failure
        }

        // --- Configure Clock (only once) ---
        const startTime = viewer.clock.currentTime; // Use current time as start for simplicity
        const stopTime = JulianDate.addSeconds(startTime, animConfig.durationSeconds, new JulianDate());
        if (!clockConfigured) {
          viewer.clock.startTime = startTime.clone();
          viewer.clock.stopTime = stopTime.clone();
          viewer.clock.currentTime = startTime.clone();
          viewer.clock.clockRange = ClockRange.LOOP_STOP;
          viewer.clock.multiplier = 1;
          clockConfigured = true; // Ensure clock is set only once
        } else {
           // Ensure subsequent animations fit within the already set clock range if durations differ
           // This might need more sophisticated handling if animations have vastly different durations/start times
           if (JulianDate.lessThan(viewer.clock.startTime, startTime)) viewer.clock.startTime = startTime.clone();
           if (JulianDate.greaterThan(viewer.clock.stopTime, stopTime)) viewer.clock.stopTime = stopTime.clone();
        }


        // --- Create Truck Entity ---
        const truckPositionProperty = new SampledPositionProperty();
        const totalPoints = cartesianPositions.length;
        for (let i = 0; i < totalPoints; i++) {
          const time = JulianDate.addSeconds(startTime, (i / (totalPoints - 1)) * animConfig.durationSeconds, new JulianDate());
          // Ensure sample time is within the global clock range if adjusted
          if (JulianDate.greaterThanOrEquals(time, viewer.clock.startTime) && JulianDate.lessThanOrEquals(time, viewer.clock.stopTime)) {
             truckPositionProperty.addSample(time, cartesianPositions[i]);
          }
        }

        localTruckEntity = viewer.entities.add({
          id: animConfig.truckEntityId, // Use ID from config
          availability: new TimeIntervalCollection([new TimeInterval({ start: viewer.clock.startTime, stop: viewer.clock.stopTime })]), // Use global clock range
          position: truckPositionProperty,
          orientation: new VelocityOrientationProperty(truckPositionProperty),
          model: {
            uri: animConfig.truckModelPath, // Use path from config
            scale: 2.5,
            heightReference: Cesium.HeightReference.NONE,
            shadows: ShadowMode.ENABLED,
          },
          billboard: {
            image: new Cesium.PinBuilder().fromText(animConfig.billboardText, Cesium.Color.BLUE, 90).toDataURL(), // Use text from config
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -15),
            heightReference: Cesium.HeightReference.NONE,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        });
        // Pass the entity ref up ONLY if it's the designated trackable one and callback exists
        if (animConfig.id === 'originalAnimation' && typeof onTruckEntityCreated === 'function') {
          onTruckEntityCreated(localTruckEntity);
        }

        // --- Create Waypoints ---
        if (cartesianPositions && cartesianPositions.length > 0) {
          const numWaypoints = 10;
          const createXSymbol = () => { /* ... (keep existing symbol creation) ... */
            const canvas = document.createElement('canvas');
            canvas.width = 32;
            canvas.height = 32;
            const context = canvas.getContext('2d');
            context.beginPath(); context.lineWidth = 4; context.strokeStyle = 'red'; context.moveTo(4, 4); context.lineTo(28, 28); context.moveTo(28, 4); context.lineTo(4, 28); context.stroke();
            context.beginPath(); context.lineWidth = 2; context.strokeStyle = 'white'; context.arc(16, 16, 14, 0, 2 * Math.PI); context.stroke();
            return canvas;
          };
          const xSymbolCanvas = createXSymbol();

          for (let i = 0; i < numWaypoints; i++) {
            const index = Math.floor(i * (cartesianPositions.length - 1) / (numWaypoints - 1));
            const position = cartesianPositions[index];
            const pointTime = JulianDate.addSeconds(startTime, (i / (numWaypoints - 1)) * animConfig.durationSeconds + 0.1, new JulianDate());
            const availability = new TimeIntervalCollection([new TimeInterval({ start: pointTime, stop: viewer.clock.stopTime })]); // Use global stop time

            const waypoint = viewer.entities.add({
              id: `pathPoint_${animConfig.id}_${i + 1}`, // Unique waypoint ID
              position: position,
              availability: availability,
              billboard: { image: xSymbolCanvas, width: 24, height: 24, scaleByDistance: new Cesium.NearFarScalar(1000, 1.0, 10000, 0.6), heightReference: Cesium.HeightReference.NONE, disableDepthTestDistance: Number.POSITIVE_INFINITY, verticalOrigin: Cesium.VerticalOrigin.CENTER },
              label: { text: `Pothole ${i + 1}`, font: '12pt sans-serif', style: Cesium.LabelStyle.FILL_AND_OUTLINE, outlineWidth: 2, verticalOrigin: Cesium.VerticalOrigin.BOTTOM, pixelOffset: new Cesium.Cartesian2(0, -15), fillColor: Cesium.Color.WHITE, outlineColor: Cesium.Color.BLACK, heightReference: Cesium.HeightReference.NONE, disableDepthTestDistance: Number.POSITIVE_INFINITY },
              properties: { potholeData: { id: i + 1, detected: new Date().toLocaleDateString(), network: "R2 Highway", location: `KM ${Math.floor(Math.random() * 50) + 100}.${Math.floor(Math.random() * 10)}`, detection: "Pothole", bound: Math.random() > 0.5 ? "Northbound" : "Southbound", severity: Math.random() > 0.7 ? "High" : Math.random() > 0.4 ? "Medium" : "Low" } }
            });
            localWaypoints.push(waypoint);

            // Schedule notification timeout if callback exists
            if (typeof onWaypointNotify === 'function') {
              const delayMs = Math.max(0, JulianDate.secondsDifference(pointTime, startTime) * 1000); // Ensure non-negative delay
              const timeoutId = setTimeout(() => {
                onWaypointNotify({ id: i + 1, animId: animConfig.id });
              }, delayMs);
              localTimeoutIds.push(timeoutId); // Store timeout ID for cleanup
            }
          }

          // --- Setup Tick Listener (only one needed globally) ---
          if (!animationRefs.current.globalTickListener) { // Check if global listener exists
             const globalTickListener = (clock) => { // Define directly, don't assign to localTickListener
                // Loop through all managed animations to update waypoints
                Object.values(animationRefs.current).forEach(animData => {
                    if (animData && animData.waypoints && animData.waypoints.length > 0) {
                        // Use the global clock times for checking loop boundaries
                        if (JulianDate.greaterThanOrEquals(clock.currentTime, JulianDate.addSeconds(viewer.clock.stopTime, -0.1, new JulianDate()))) {
                            animData.waypoints.forEach(wp => { wp.show = false; });
                        } else if (JulianDate.lessThanOrEquals(clock.currentTime, JulianDate.addSeconds(viewer.clock.startTime, 0.5, new JulianDate()))) {
                            animData.waypoints.forEach(wp => { wp.show = true; });
                        }
                    }
                });
             };
             viewer.clock.onTick.addEventListener(globalTickListener);
             animationRefs.current.globalTickListener = globalTickListener; // Store globally
          }
        }

        // Return created objects for this animation
        return {
          dataSource: localDataSource,
          truckEntity: localTruckEntity,
          waypoints: localWaypoints,
          timeoutIds: localTimeoutIds, // Include timeout IDs in the returned object
          // tickListener is handled globally now
        };

      } catch (error) {
        console.error(`AnimationManager [${animConfig.id}]: Error setting up animation:`, error);
        // Clean up partially added things for this specific animation
        if (localDataSource && viewer.dataSources.contains(localDataSource)) {
          viewer.dataSources.remove(localDataSource, true);
        }
        if (localTruckEntity && viewer.entities.contains(localTruckEntity)) {
           viewer.entities.remove(localTruckEntity);
        }
        localWaypoints.forEach(wp => { if (viewer.entities.contains(wp)) viewer.entities.remove(wp); });
        // Also clear any pending timeouts for this failed setup
        localTimeoutIds.forEach(clearTimeout);
        return null; // Indicate failure
      }
    };


    // Only setup animation if the trigger prop is valid (not null)
    // --- Main Effect Logic ---
    if (shouldLoadAnimations) { // Check the new boolean prop
      // Ensure cleanup happens first if re-triggering
      cleanupAnimations();

      // Load all configured animations
      const setupPromises = mapConfig.animations.map(async (animConfig) => {
        const result = await setupSingleAnimation(animConfig);
        if (result) {
          animationRefs.current[animConfig.id] = result; // Store successful setup
        }
      });

      // Wait for all setups to complete (or fail)
      Promise.all(setupPromises).then(() => {
         // Start animation only if at least one setup was successful and clock was configured
         if (clockConfigured && Object.keys(animationRefs.current).length > 0) {
             viewer.clock.shouldAnimate = true;
         }
      });

    }

    // Cleanup function runs on unmount or when dependencies change
    return cleanupAnimations;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerRef, shouldLoadAnimations]);

  // This component doesn't render anything itself
  return null;
};

export default AnimationManager;